import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '@/lib/auth/supabaseAuth';
import { markIntentionalSignOut } from '@/app/providers';
import {
  useDashboardStats,
  usePipelineAlerts,
  useSourcingProgress,
  useActivityFeed,
  useJobOutreachSettings,
  useUpdateJobOutreachTopN,
  useSendNextOutreachBatch,
  reportApiError,
} from '@/lib/api/apiClient';
import { Skeleton } from '@/components/ui/skeleton';
import type { PipelineAlertRow } from '@/lib/validation/pipelineAlerts';
import { ACTIVITY_FEED_EVENT_LABELS } from '@/lib/validation/activityFeed';
import type { ZeroQueuedReasonBreakdownResponse } from '@/lib/validation/sourcingProgress';

// NOTE: Do NOT construct the Supabase client at module scope here (it used
// to be `createClient(import.meta.env.VITE_SUPABASE_URL || '', ...)`, which
// runs at import time). `Dashboard.tsx` is statically imported by
// `src/app/router.tsx`, which is evaluated in Node during
// `vite-react-ssg build` in addition to in the browser — a missing env var
// there would throw ("supabaseUrl is required") and abort the entire
// static build, not just this page. Use the shared lazily-initialized
// client from `lib/auth/supabaseAuth.ts` instead, called at use time.
//
// Logout redirect target (GitHub issue #494 — "E2E: Authentication —
// login, signup, OTP, password reset, session management", "Session
// Management" test-case group: "Logout clears localStorage and redirects
// to /login"): `handleLogout` used to `navigate('/')` after signing out.
// `supabase.auth.signOut()` already clears the persisted
// `sb-<ref>-auth-token` localStorage entry (Supabase Auth SDK's own
// `_signOut`/`_removeSession`, not app code) — only the redirect target
// changed here, to match this ticket's explicit AC. See
// `e2e/session-management-auth-flow.spec.ts`.
//
// Stat tiles (GitHub issue "bug: regular users see wrong dashboard
// numbers — admin-only stats endpoint" — Luisa Franco / LFP Risk
// Solutions): this page used to render hard-coded `0`s for every tile
// behind a "Welcome to Hero Hire AI v2.0" placeholder — the only real
// stats endpoints (`GET /api/admin/dashboard`, `GET /api/admin/jobs`)
// required superuser status, so a regular customer's dashboard never
// showed their real counts at all. Now wired to `useDashboardStats()`
// (`@/lib/api/apiClient`), which calls the new user-scoped
// `GET /api/dashboard/stats` (`apps/workers/src/api/dashboardStats.ts`,
// any authenticated user, never a 403) and is scoped to the caller's own
// jobs via Postgres RLS.
/**
 * Human-facing message for one `pipeline_alerts` row (GitHub issue "P1:
 * No stale pipeline alerting"). The `zero_outreach_3d` wording
 * deliberately contains the exact phrase "zero outreach for 3+ days" —
 * this ticket's own demo acceptance criterion: "a visible stale-pipeline
 * warning banner ... with text indicating zero outreach for 3+ days".
 */
function pipelineAlertMessage(alert: PipelineAlertRow): string {
  switch (alert.type) {
    case 'zero_outreach_3d': {
      const rawDays = alert.details.daysSinceLastOutreach;
      const days = typeof rawDays === 'number' ? rawDays.toFixed(1) : null;
      return days
        ? `Zero outreach for 3+ days (${days} days since last outreach)`
        : 'Zero outreach for 3+ days';
    }
    case 'zero_reply_rate_10_sends':
      return '0% reply rate after 10+ outreach sends';
    case 'pipeline_gap':
      return 'Job description ready but sourcing was never started';
    default:
      return 'This job needs attention';
  }
}

/**
 * Candidate-stage explanations for the dashboard's "evaluating /
 * contacted / shortlisted" tooltips — GitHub issue "P0: Customer churn —
 * Tom (25 days, zero interviews, requesting refund) — dashboard stale,
 * pipeline broken", item #2 of "What Tom explicitly asked for": "A real
 * explanation of what the numbers mean". Every candidate Tom's dashboard
 * used to show a bare number for now gets a plain-English definition on
 * hover.
 */
const STAGE_EXPLANATIONS = {
  evaluating: 'Candidates our AI has screened against your job criteria and scored for fit.',
  contacted: 'Candidates who have been sent a CONFIRMED outreach email (not just queued).',
  shortlisted: 'Candidates who replied with interest and are ready for you to review or interview.',
} as const;

/** Format an ISO timestamp as a short, human-readable "last updated" string. */
function formatLastUpdated(iso: string | null): string {
  if (!iso) return 'No activity yet';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Small inline tooltip: always renders the explanation in the DOM
 * (visible on hover via `group-hover`), so both a real browser's mouse
 * hover AND any DOM-text-based check can find the explanatory copy. */
function StageLabelWithTooltip({ label, explanation, testId }: { label: string; explanation: string; testId: string }) {
  return (
    <span className="group relative inline-flex items-center gap-1" data-testid={testId}>
      <span className="cursor-help underline decoration-dotted" title={explanation}>
        {label}
      </span>
      <span
        role="tooltip"
        data-testid={`${testId}-tooltip`}
        className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-md border border-border bg-popover p-2 text-xs font-normal text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100"
      >
        {explanation}
      </span>
    </span>
  );
}

/**
 * "Top-N outreach targeting" job-dashboard panel (GitHub issue "[Email]
 * Top-N targeting — queue outreach only for the top-N best-fit
 * candidates per job", milestone "Email & Outreach Overhaul").
 *
 * - Lets the job's owner (or a teammate) configure how many best-fit
 *   candidates the sourcing pipeline queues for outreach per run
 *   (`jobs.outreach_top_n`, `GET`/`PATCH /api/jobs/:jobId`, default 25).
 * - Shows the current queued-outreach count (`sourcingProgress.
 *   candidatesQueued` — the same honest, never-overstated count already
 *   surfaced elsewhere on this page, see `../lib/api/apiClient.ts`'s
 *   `useSourcingProgress`/`sourcingProgressMetrics.ts`).
 * - "Send next batch" advances the next `outreach_top_n` best-fit,
 *   still-screened candidates into `OUTREACH_QUEUED` on demand
 *   (`POST /api/jobs/:jobId/outreach/next-batch`), without re-running
 *   the whole sourcing pipeline.
 */
function OutreachTargetingPanel({
  jobId,
  candidatesQueued,
  zeroQueuedReasonBreakdown,
}: {
  jobId: string;
  candidatesQueued: number;
  /** GitHub issue #195/#522 ("[Email/UX] 'Sourcing complete — 0 ready
   * for outreach' must explain WHY") — non-null only when
   * `candidatesQueued` is 0. See this component's render below and
   * `apps/workers/src/lib/zeroQueuedReasonBreakdown.ts` for the
   * derivation. */
  zeroQueuedReasonBreakdown?: ZeroQueuedReasonBreakdownResponse | null;
}) {
  const { data: settings, isLoading: isSettingsLoading } = useJobOutreachSettings(jobId);
  const updateTopN = useUpdateJobOutreachTopN();
  const sendNextBatch = useSendNextOutreachBatch();

  // Local, editable copy of the top-N input — seeded from the fetched
  // setting once it arrives, but never silently overwritten again after
  // that (so a user's in-progress edit isn't clobbered by a background
  // refetch).
  const [topNInput, setTopNInput] = useState<string>('');
  const [hasSeededInput, setHasSeededInput] = useState(false);

  useEffect(() => {
    if (settings && !hasSeededInput) {
      setTopNInput(String(settings.outreachTopN));
      setHasSeededInput(true);
    }
  }, [settings, hasSeededInput]);

  const handleSaveTopN = () => {
    const parsed = Number.parseInt(topNInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    updateTopN.mutate({ jobId, outreachTopN: parsed });
  };

  return (
    <div
      className="mt-8 p-6 border border-border rounded-lg"
      data-testid="outreach-top-n-panel"
    >
      <h2 className="text-xl font-semibold mb-4">Outreach Targeting</h2>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label htmlFor="outreach-top-n-input" className="text-sm font-medium">
          Top-N candidates to contact per job
        </label>
        <input
          id="outreach-top-n-input"
          type="number"
          min={1}
          data-testid="outreach-top-n-input"
          className="w-24 rounded-md border border-border px-2 py-1 text-sm"
          value={topNInput}
          disabled={isSettingsLoading}
          onChange={(e) => setTopNInput(e.target.value)}
        />
        <button
          onClick={handleSaveTopN}
          disabled={updateTopN.isPending}
          data-testid="outreach-top-n-save-button"
          className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
        >
          Save
        </button>
      </div>

      <p className="text-sm text-muted-foreground mb-2" data-testid="outreach-top-n-current-value">
        Configured top-N: <span className="font-semibold">{settings?.outreachTopN ?? '—'}</span>
      </p>

      <p className="text-sm text-muted-foreground mb-4" data-testid="queued-outreach-count">
        Queued outreach for this job: <span className="font-semibold">{candidatesQueued}</span>
      </p>

      {/* GitHub issue #195/#522: a bare "Queued 0" reads as broken even
          when the mechanics worked ("Sourced 10 / Screened 278 / Queued
          0" with no explanation). Whenever this job's latest run queued
          0, show WHY — the reason breakdown the backend derived from
          real `matches.decline_reason` data, or a visible placeholder if
          that data is unavailable. Never shown when `candidatesQueued`
          is >0 (this issue's [qa:4]). */}
      {candidatesQueued === 0 && (
        <div
          className="mb-4 rounded-md border border-border bg-muted/40 p-3 text-sm"
          data-testid="zero-queued-reason-breakdown"
        >
          <p className="font-medium text-foreground">
            {zeroQueuedReasonBreakdown?.available
              ? zeroQueuedReasonBreakdown.breakdownText
              : 'Reason breakdown unavailable'}
          </p>
          {!!zeroQueuedReasonBreakdown?.available &&
            zeroQueuedReasonBreakdown.needsEmailEnrichmentCount !== null &&
            zeroQueuedReasonBreakdown.needsEmailEnrichmentCount > 0 && (
              <span
                className="group relative mt-1 inline-flex items-center gap-1 text-muted-foreground"
                data-testid="needs-email-enrichment-count"
              >
                <span className="cursor-help underline decoration-dotted" title="Candidates lacking a deliverable email address">
                  {zeroQueuedReasonBreakdown.needsEmailEnrichmentCount} need email enrichment
                </span>
                <span
                  role="tooltip"
                  data-testid="needs-email-enrichment-tooltip"
                  className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-md border border-border bg-popover p-2 text-xs font-normal text-popover-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100"
                >
                  Candidates screened but lacking a deliverable email address — enrich these to
                  unlock more outreach.
                </span>
              </span>
            )}
        </div>
      )}

      <button
        onClick={() => sendNextBatch.mutate({ jobId })}
        disabled={sendNextBatch.isPending}
        data-testid="send-next-batch-button"
        className="px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
      >
        Send next batch
      </button>

      {sendNextBatch.isSuccess && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="send-next-batch-result">
          Queued {sendNextBatch.data.queued_count} more candidate
          {sendNextBatch.data.queued_count === 1 ? '' : 's'} for outreach.
        </p>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch, isFetching } = useDashboardStats();
  const { data: alertsData } = usePipelineAlerts();
  const staleAlerts = alertsData?.alerts ?? [];

  // GitHub issue "P0: Customer churn — Tom ... dashboard stale, pipeline
  // broken": drive the per-job sourcing-progress + activity-feed panels
  // off the caller's own most-recently-created job (`primaryJobId`,
  // returned by `useDashboardStats()` — see that hook's doc-comment).
  const primaryJobId = data?.primaryJobId ?? undefined;
  const {
    data: sourcingProgress,
    isLoading: isSourcingProgressLoading,
    isError: isSourcingProgressError,
  } = useSourcingProgress(primaryJobId);
  const { data: activityFeed, isLoading: isActivityFeedLoading } = useActivityFeed(primaryJobId);

  useEffect(() => {
    if (isError) {
      reportApiError(error, 'Failed to load dashboard stats');
    }
  }, [isError, error]);

  const handleLogout = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      navigate('/login');
      return;
    }
    // Flag this as an intentional, user-initiated sign-out *before* it
    // fires, so `AuthProvider`'s auth-state-change listener doesn't
    // mistake it for an involuntary session expiry (see
    // `markIntentionalSignOut()`'s doc-comment in `@/app/providers`).
    markIntentionalSignOut();
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 border border-border rounded-md hover:bg-accent"
          >
            Sign Out
          </button>
        </div>
      </header>

      {staleAlerts.length > 0 && (
        <div
          className="container mx-auto px-4 pt-6"
          data-testid="pipeline-alerts-panel"
          role="alert"
        >
          {staleAlerts.map((alert) => (
            <div
              key={alert.id}
              data-testid="stale-pipeline-alert"
              className="mb-3 p-4 border border-destructive/40 bg-destructive/10 rounded-lg flex items-start gap-2"
            >
              <span aria-hidden="true">⚠️</span>
              <p className="text-sm text-destructive">
                <span className="font-semibold">{alert.jobTitle}:</span>{' '}
                {pipelineAlertMessage(alert)}
              </p>
            </div>
          ))}
        </div>
      )}

      <main className="container mx-auto px-4 py-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-busy={isLoading}>
          {isLoading ? (
            <>
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </>
          ) : isError || !data ? (
            <div className="p-6 border border-border rounded-lg md:col-span-2 lg:col-span-3">
              <p className="text-sm text-destructive font-medium">
                Couldn&apos;t load your dashboard stats
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {error instanceof Error ? error.message : 'Please try again.'}
              </p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-3 px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="p-6 border border-border rounded-lg" data-testid="stat-sourced-contacts">
                <h2 className="text-xl font-semibold mb-2">Sourced Contacts</h2>
                <p className="text-3xl font-bold">{data.sourcedCount}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data.sourcedCount === 0
                    ? 'No candidates sourced yet'
                    : 'Candidates sourced across all your jobs'}
                </p>
              </div>

              <div className="p-6 border border-border rounded-lg" data-testid="stat-active-pipeline">
                <h2 className="text-xl font-semibold mb-2">Active Pipeline</h2>
                <p className="text-3xl font-bold">{data.activePipelineCount}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data.activePipelineCount === 0
                    ? 'No candidates in your pipeline'
                    : 'Candidates currently being pursued'}
                </p>
              </div>

              <div className="p-6 border border-border rounded-lg" data-testid="stat-open-conversations">
                <h2 className="text-xl font-semibold mb-2">Open Conversations</h2>
                <p className="text-3xl font-bold">{data.openConversationsCount}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {data.openConversationsCount === 0
                    ? 'No open conversations'
                    : 'Candidates who have replied'}
                </p>
              </div>
            </>
          )}
        </div>

        {primaryJobId && (
          <div
            className="mt-8 p-6 border border-border rounded-lg"
            data-testid="candidate-pipeline-panel"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <h2 className="text-xl font-semibold">
                Candidate Pipeline{data?.primaryJobTitle ? ` — ${data.primaryJobTitle}` : ''}
              </h2>
              {!isSourcingProgressLoading && !isSourcingProgressError && (
                <p className="text-xs text-muted-foreground" data-testid="pipeline-last-updated">
                  Last updated: {formatLastUpdated(sourcingProgress?.lastActivityAt ?? null)}
                </p>
              )}
            </div>

            {isSourcingProgressLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : isSourcingProgressError || !sourcingProgress ? (
              <p
                className="text-sm text-destructive"
                data-testid="pipeline-error"
                role="alert"
              >
                Unable to load pipeline status. Please try again.
              </p>
            ) : (
              <>
                {sourcingProgress.stalled && (
                  <p
                    className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
                    data-testid="pipeline-stalled-warning"
                  >
                    ⚠️ This pipeline hasn&apos;t made progress recently — we&apos;re looking into it.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div data-testid="pipeline-count-evaluating">
                    <p className="text-2xl font-bold">{sourcingProgress.candidatesScreened}</p>
                    <p className="text-sm text-muted-foreground">
                      <StageLabelWithTooltip
                        label="Evaluating"
                        explanation={STAGE_EXPLANATIONS.evaluating}
                        testId="stage-label-evaluating"
                      />
                    </p>
                  </div>
                  <div data-testid="pipeline-count-contacted">
                    <p className="text-2xl font-bold">{sourcingProgress.candidatesContacted}</p>
                    <p className="text-sm text-muted-foreground">
                      <StageLabelWithTooltip
                        label="Contacted"
                        explanation={STAGE_EXPLANATIONS.contacted}
                        testId="stage-label-contacted"
                      />
                    </p>
                  </div>
                  <div data-testid="pipeline-count-shortlisted">
                    <p className="text-2xl font-bold">{sourcingProgress.candidatesShortlisted}</p>
                    <p className="text-sm text-muted-foreground">
                      <StageLabelWithTooltip
                        label="Shortlisted"
                        explanation={STAGE_EXPLANATIONS.shortlisted}
                        testId="stage-label-shortlisted"
                      />
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {primaryJobId && (
          <OutreachTargetingPanel
            jobId={primaryJobId}
            candidatesQueued={sourcingProgress?.candidatesQueued ?? 0}
            zeroQueuedReasonBreakdown={sourcingProgress?.zeroQueuedReasonBreakdown ?? null}
          />
        )}

        {primaryJobId && (
          <div
            className="mt-8 p-6 border border-border rounded-lg"
            data-testid="activity-feed-panel"
          >
            <h2 className="text-xl font-semibold mb-4">Activity Feed</h2>
            {isActivityFeedLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : !activityFeed || activityFeed.events.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="activity-feed-empty">
                No activity yet — check back once outreach begins.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="activity-feed-list">
                {activityFeed.events.map((event) => (
                  <li
                    key={`${event.messageId}-${event.type}-${event.timestamp}`}
                    className="flex items-start justify-between gap-4 border-b border-border pb-2 last:border-b-0"
                    data-testid="activity-feed-event"
                  >
                    <div>
                      <span className="text-xs font-semibold uppercase text-muted-foreground mr-2">
                        {ACTIVITY_FEED_EVENT_LABELS[event.type]}
                      </span>
                      <span className="text-sm">{event.message}</span>
                    </div>
                    <time
                      dateTime={event.timestamp}
                      className="whitespace-nowrap text-xs text-muted-foreground"
                    >
                      {formatLastUpdated(event.timestamp)}
                    </time>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <div className="mt-8 p-6 border border-border rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Welcome to Hero Hire AI v2.0</h2>
          <p className="text-muted-foreground">
            This is the new architecture powered by Supabase, Cloudflare Workers, and Modal.
          </p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>✓ Supabase Auth + RLS for security</li>
            <li>✓ Cloudflare Workers for edge API routes</li>
            <li>✓ Modal for AI compute (sourcing, matching, embeddings)</li>
            <li>✓ React + Vite + shadcn/ui for modern UI</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
